import { useEffect, useState } from "react";
import {
  calculateFoodBaskets,
  fetchFoodBasketResults,
  fetchFoodBaskets,
} from "../../../../api/foodApi";
import {
  foodRetailSourceLabel,
  isFoodRetailPlaybookSource,
} from "../../../../constants/foodSourceConstants";
import { formatCop } from "../../../../constants/food";
import AdminTableContainer from "../layout/AdminTableContainer";

const FoodAraBasketCalculationSection = ({ sourceKey }) => {
  const [baskets, setBaskets] = useState([]);
  const [basketId, setBasketId] = useState("");
  const [freshnessWindowHours, setFreshnessWindowHours] = useState(72);
  const [preferPromotionPrice, setPreferPromotionPrice] = useState(true);
  const [dryRun, setDryRun] = useState(true);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const sourceLabel = foodRetailSourceLabel(sourceKey);

  useEffect(() => {
    fetchFoodBaskets()
      .then((data) => {
        const list = data.baskets || [];
        setBaskets(list);
        const defaultBasket = list.find((b) => b.isDefault) || list[0];
        if (defaultBasket) setBasketId(defaultBasket.id);
      })
      .catch(() => {});
  }, []);

  const loadHistory = async () => {
    if (!sourceKey) return;
    const payload = await fetchFoodBasketResults({
      sourceKey,
      basketId: basketId || undefined,
      limit: 5,
    });
    setHistory(payload.results || []);
  };

  const handleCalculate = async () => {
    if (!sourceKey) return;
    try {
      setRunning(true);
      setErrorMessage("");
      const payload = await calculateFoodBaskets({
        sourceKey,
        basketId: basketId || undefined,
        freshnessWindowHours: Number(freshnessWindowHours) || 72,
        preferPromotionPrice,
        dryRun,
      });
      setResult(payload);
      if (!dryRun) {
        await loadHistory();
      }
    } catch (error) {
      setErrorMessage(error.message || "Basket calculation failed.");
    } finally {
      setRunning(false);
    }
  };

  useEffect(() => {
    if (basketId && sourceKey) {
      loadHistory().catch(() => {});
    }
  }, [basketId, sourceKey]);

  if (!sourceKey || !isFoodRetailPlaybookSource(sourceKey)) {
    return null;
  }

  return (
    <section className="panel-card food-retail-ingestion-page__normalization">
      <div className="food-retail-ingestion-page__section-header">
        <div>
          <h2>{sourceLabel} Basic Basket Calculation</h2>
          <p className="muted">
            Calculate the Basic Colombian Household Basket from normalized {sourceLabel} observations.
          </p>
        </div>
      </div>

      <div className="food-retail-ingestion-page__grid">
        <label>
          Basket
          <select value={basketId} onChange={(event) => setBasketId(event.target.value)}>
            {baskets.map((basket) => (
              <option key={basket.id} value={basket.id}>
                {basket.basketName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Source
          <input value={sourceKey} readOnly />
        </label>
        <label>
          Freshness window (hours)
          <input
            type="number"
            min={1}
            max={720}
            value={freshnessWindowHours}
            onChange={(event) => setFreshnessWindowHours(event.target.value)}
          />
        </label>
      </div>

      <label className="food-comparison-filters__checkbox food-retail-ingestion-page__confirm">
        <input
          type="checkbox"
          checked={preferPromotionPrice}
          onChange={(event) => setPreferPromotionPrice(event.target.checked)}
        />
        Prefer promotion price when available
      </label>

      <label className="food-comparison-filters__checkbox food-retail-ingestion-page__confirm">
        <input
          type="checkbox"
          checked={dryRun}
          onChange={(event) => setDryRun(event.target.checked)}
        />
        Dry run (do not persist)
      </label>

      <div className="food-retail-ingestion-page__enablement-buttons">
        <button
          type="button"
          className="btn btn--primary btn--sm"
          disabled={running}
          onClick={handleCalculate}
        >
          {running ? "Calculating…" : "Calculate Basket"}
        </button>
      </div>

      {errorMessage ? <p className="food-retail-ingestion-page__form-errors">{errorMessage}</p> : null}

      {result ? (
        <dl className="food-retail-ingestion-page__readiness-grid">
          <div>
            <dt>Status</dt>
            <dd>{result.status}</dd>
          </div>
          <div>
            <dt>Total cost</dt>
            <dd>{formatCop(result.totalCostCop)}</dd>
          </div>
          <div>
            <dt>Coverage</dt>
            <dd>{result.coveragePercent ?? Math.round((result.coverageRatio || 0) * 100)}%</dd>
          </div>
          <div>
            <dt>Priced items</dt>
            <dd>{result.pricedItemCount ?? 0}</dd>
          </div>
          <div>
            <dt>Missing items</dt>
            <dd>{result.missingItemsCount ?? 0}</dd>
          </div>
          <div>
            <dt>Needs review</dt>
            <dd>{result.needsReviewCount ?? 0}</dd>
          </div>
        </dl>
      ) : null}

      {result?.items?.length ? (
        <AdminTableContainer>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Requested</th>
                <th>Selected product</th>
                <th>Item cost</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {result.items.map((item) => (
                <tr key={`${item.basketItemId}-${item.productId}`}>
                  <td>{item.productName || item.basketItem}</td>
                  <td>
                    {item.requiredQuantity} {item.requiredUnit}
                  </td>
                  <td>{item.rawProductName || "—"}</td>
                  <td>{item.itemCostCop != null ? formatCop(item.itemCostCop) : "—"}</td>
                  <td>{item.matchStatus}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </AdminTableContainer>
      ) : null}

      {history.length ? (
        <>
          <h3>Latest calculation history</h3>
          <ul className="food-retail-ingestion-page__blocked">
            {history.map((row) => (
              <li key={row.id}>
                {row.calculatedAt ? new Date(row.calculatedAt).toLocaleString() : "—"} —{" "}
                {formatCop(row.totalCostCop)} ({row.calculationStatus})
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </section>
  );
};

export default FoodAraBasketCalculationSection;
